import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import "../../styles/CustomYouTubePlayer.css";

function formatTime(seconds) {
  const total = Number.isFinite(seconds)
    ? Math.max(0, Math.floor(seconds))
    : 0;

  const minutes = Math.floor(total / 60);
  const remainingSeconds = total % 60;

  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

const QUALITY_LABELS = {
  highres: "4320p",
  hd2880: "2880p",
  hd2160: "2160p",
  hd1440: "1440p",
  hd1080: "1080p",
  hd720: "720p",
  large: "480p",
  medium: "360p",
  small: "240p",
  tiny: "144p",
};

function PlayerIcon({ name }) {
  const paths = {
    play: <path d="m8 5 12 7-12 7z" />,

    pause: (
      <>
        <path d="M8 5h3v14H8z" />
        <path d="M13 5h3v14h-3z" />
      </>
    ),

    volume: (
      <>
        <path d="M4 10v4h4l5 4V6l-5 4H4z" />
        <path d="M16 9a5 5 0 0 1 0 6m2-8a8 8 0 0 1 0 10" />
      </>
    ),

    muted: (
      <>
        <path d="M4 10v4h4l5 4V6l-5 4H4z" />
        <path d="m17 9 5 6m0-6-5 6" />
      </>
    ),

    fullscreen: (
      <path d="M8 3H5a2 2 0 0 0-2 2v3m13-5h3a2 2 0 0 1 2 2v3M3 16v3a2 2 0 0 0 2 2h3m13-5v3a2 2 0 0 1-2 2h-3" />
    ),

    exitFullscreen: (
      <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3m8 0v-3a2 2 0 0 1 2-2h3" />
    ),

    rewind: (
      <>
        <path d="M4 8V4m0 4h4M4.9 8.1A8 8 0 1 1 4 12" />
        <path d="M12 8v8m-2-6 2-2 2 2" />
      </>
    ),

    forward: (
      <>
        <path d="M20 8V4m0 4h-4m3.1.1A8 8 0 1 0 20 12" />
        <path d="M12 8v8m-2-6 2-2 2 2" />
      </>
    ),
  };

  return (
    <svg
      className={[
        "volume",
        "muted",
        "fullscreen",
        "exitFullscreen",
        "rewind",
        "forward",
      ].includes(name)
        ? "cd-player-icon-outline"
        : ""}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      {paths[name]}
    </svg>
  );
}

export default function OnlyPlayer({
  videoUrl,
  title = "Video player",
  className = "",
  autoplay = false,
  autoPlayWhenVisible = false,
  mutedAutoplay = true,
  onPlay,
  onPause,
}) {
  const rootRef = useRef(null);
  const playerRef = useRef(null);
  const visibleRef = useRef(false);
  const audioPreferenceChangedRef = useRef(false);
  const controlsHideTimeoutRef = useRef(null);

  const [state, setState] = useState("loading");
  const [playing, setPlaying] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(100);

  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const [playbackRate, setPlaybackRate] = useState(1);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState("");

  const clearControlsHideTimeout = useCallback(() => {
    if (controlsHideTimeoutRef.current) {
      clearTimeout(controlsHideTimeoutRef.current);
      controlsHideTimeoutRef.current = null;
    }
  }, []);

  const showControls = useCallback(() => {
    setControlsVisible(true);
    clearControlsHideTimeout();

    if (playing && state === "ready") {
      controlsHideTimeoutRef.current = setTimeout(() => {
        controlsHideTimeoutRef.current = null;
        setControlsVisible(false);
      }, 2500);
    }
  }, [clearControlsHideTimeout, playing, state]);

  useEffect(() => {
    if (playing && state === "ready") {
      showControls();
    } else {
      clearControlsHideTimeout();
      setControlsVisible(true);
    }
  }, [clearControlsHideTimeout, playing, showControls, state]);

  useEffect(() => clearControlsHideTimeout, [clearControlsHideTimeout]);

  const callbacksRef = useRef({
    onPlay,
    onPause,
    autoplay,
    autoPlayWhenVisible,
    mutedAutoplay,
  });

  useEffect(() => {
    callbacksRef.current = {
      onPlay,
      onPause,
      autoplay,
      autoPlayWhenVisible,
      mutedAutoplay,
    };
  }, [
    onPlay,
    onPause,
    autoplay,
    autoPlayWhenVisible,
    mutedAutoplay,
  ]);

  /*
   * ---------------------------------------------------------
   * PLAYER EVENTS
   * ---------------------------------------------------------
   */

  const handleReady = useCallback(() => {
    setState("ready");
    setError("");

    const player = playerRef.current;

    if (!player) return;

    const shouldAutoplay =
      callbacksRef.current.autoplay ||
      (
        callbacksRef.current.autoPlayWhenVisible &&
        visibleRef.current
      );

    if (
      shouldAutoplay &&
      callbacksRef.current.mutedAutoplay &&
      !audioPreferenceChangedRef.current
    ) {
      player.muted = true;
      setMuted(true);
    }

    if (shouldAutoplay) {
      setPlaying(true);
    }
  }, []);

  const handlePlay = useCallback(() => {
    setState("ready");
    setPlaying(true);
    setControlsVisible(true);
    callbacksRef.current.onPlay?.();
  }, []);

  const handlePause = useCallback(() => {
    setPlaying(false);
    setControlsVisible(true);
    callbacksRef.current.onPause?.();
  }, []);

  const handleEnded = useCallback(() => {
    setPlaying(false);
    setControlsVisible(true);

    if (duration) {
      setCurrentTime(duration);
    }

    callbacksRef.current.onPause?.();
  }, [duration]);

  const handleProgress = useCallback((event) => {
    setCurrentTime(event.currentTarget.currentTime || 0);
  }, []);

  const handleDuration = useCallback((event) => {
    setDuration(event.currentTarget.duration || 0);
  }, []);

  const handleError = useCallback(() => {
    setState("error");
    setError(
      "This video is unavailable or does not allow playback here."
    );
  }, []);

  /*
   * ---------------------------------------------------------
   * PLAY / PAUSE
   * ---------------------------------------------------------
   */

  const play = useCallback(() => {
    setPlaying(true);
  }, []);

  const pause = useCallback(() => {
    setPlaying(false);
  }, []);

  const togglePlay = () => {
    if (playing) {
      pause();
    } else {
      play();
    }
  };

  /*
   * ---------------------------------------------------------
   * MUTE / VOLUME
   * ---------------------------------------------------------
   */

  const toggleMute = () => {
    const player = playerRef.current;

    if (!player) return;

    audioPreferenceChangedRef.current = true;

    if (muted) {
      player.muted = false;
      player.volume = (volume || 50) / 100;
      setMuted(false);
    } else {
      player.muted = true;
      setMuted(true);
    }
  };

  const changeVolume = (event) => {
    const nextVolume = Number(event.target.value);

    setVolume(nextVolume);
    audioPreferenceChangedRef.current = true;

    const player = playerRef.current;

    if (!player) return;

    player.volume = nextVolume / 100;

    if (nextVolume > 0) {
      player.muted = false;
      setMuted(false);
    } else {
      player.muted = true;
      setMuted(true);
    }
  };

  /*
   * ---------------------------------------------------------
   * SEEK
   * ---------------------------------------------------------
   */

  const seek = (event) => {
    if (!duration) return;

    const percentage = Number(event.target.value);

    const nextTime = (percentage / 100) * duration;

    if (playerRef.current) {
      playerRef.current.currentTime = nextTime;
    }

    setCurrentTime(nextTime);
  };

  const seekBy = (offsetSeconds) => {
    const player = playerRef.current;

    if (!player) return;

    const current = player.currentTime || 0;
    const total = player.duration || duration || 0;

    let nextTime = Math.max(
      0,
      current + offsetSeconds
    );

    if (total) {
      nextTime = Math.min(nextTime, total);
    }

    player.currentTime = nextTime;

    setCurrentTime(nextTime);
  };

  /*
   * ---------------------------------------------------------
   * PLAYBACK SPEED
   * ---------------------------------------------------------
   */

  const changePlaybackRate = (event) => {
    const nextRate = Number(event.target.value);

    setPlaybackRate(nextRate);
  };

  /*
   * ---------------------------------------------------------
   * FULLSCREEN
   * ---------------------------------------------------------
   */

  const toggleFullscreen = () => {
    const container = rootRef.current;

    if (!container) return;

    if (document.fullscreenElement === container) {
      document.exitFullscreen?.();
    } else {
      container.requestFullscreen?.();
    }
  };

  useEffect(() => {
    const syncFullscreenState = () => {
      setIsFullscreen(
        document.fullscreenElement === rootRef.current
      );
    };

    document.addEventListener(
      "fullscreenchange",
      syncFullscreenState
    );

    return () => {
      document.removeEventListener(
        "fullscreenchange",
        syncFullscreenState
      );
    };
  }, []);

  /*
   * ---------------------------------------------------------
   * AUTO PLAY WHEN VISIBLE
   * ---------------------------------------------------------
   */

  useEffect(() => {
    if (
      !autoPlayWhenVisible ||
      !rootRef.current ||
      !window.IntersectionObserver
    ) {
      return undefined;
    }

    const target = rootRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        visibleRef.current = entry.isIntersecting;

        if (state !== "ready") return;

        if (entry.isIntersecting) {
          if (
            mutedAutoplay &&
            !audioPreferenceChangedRef.current
          ) {
            if (playerRef.current) {
              playerRef.current.muted = true;
            }
            setMuted(true);
          }

          setPlaying(true);
        } else {
          setPlaying(false);
        }
      },
      {
        threshold: 0.25,
      }
    );

    observer.observe(target);

    return () => observer.disconnect();
  }, [
    autoPlayWhenVisible,
    mutedAutoplay,
    state,
  ]);

  /*
   * ---------------------------------------------------------
   * KEYBOARD CONTROLS
   * ---------------------------------------------------------
   */

  useEffect(() => {
    const handleKeyboard = (event) => {
      const target = event.target;

      if (
        target?.tagName === "INPUT" ||
        target?.tagName === "SELECT" ||
        target?.tagName === "TEXTAREA"
      ) {
        return;
      }

      if (!rootRef.current) return;

      const rect = rootRef.current.getBoundingClientRect();

      const isVisible =
        rect.bottom > 0 &&
        rect.top < window.innerHeight;

      if (!isVisible) return;

      switch (event.code) {
        case "Space":
          event.preventDefault();
          togglePlay();
          break;

        case "ArrowLeft":
          event.preventDefault();
          seekBy(-10);
          break;

        case "ArrowRight":
          event.preventDefault();
          seekBy(10);
          break;

        case "KeyM":
          event.preventDefault();
          toggleMute();
          break;

        case "KeyF":
          event.preventDefault();
          toggleFullscreen();
          break;

        default:
          break;
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyboard
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyboard
      );
    };
  });

  /*
   * ---------------------------------------------------------
   * VALIDATION
   * ---------------------------------------------------------
   */

  if (!videoUrl) {
    return (
      <div
        ref={rootRef}
        className={`cd-custom-player${controlsVisible ? "" : " cd-controls-hidden"} ${className}`}
        role="region"
        aria-label={title}
        onPointerMove={showControls}
        onPointerDown={showControls}
        onTouchStart={showControls}
      >
        <div className="cd-custom-player-video">
          <div className="cd-custom-player-message cd-custom-player-error">
            This video link is invalid.
          </div>
        </div>
      </div>
    );
  }

  /*
   * ---------------------------------------------------------
   * RENDER
   * ---------------------------------------------------------
   */

  return (
    <div
      ref={rootRef}
      className={`cd-custom-player${controlsVisible ? "" : " cd-controls-hidden"} ${className}`}
      role="region"
      aria-label={title}
      onPointerMove={showControls}
      onPointerDown={showControls}
      onTouchStart={showControls}
    >
      <div className="cd-custom-player-video">

        {/* ReactPlayer replaces YouTube IFrame API */}
        <ReactPlayer
          ref={playerRef}
          src={videoUrl}
          width="100%"
          height="100%"
          playing={playing}
          muted={muted}
          volume={volume / 100}
          playbackRate={playbackRate}
          controls={false}
          playsInline
          onReady={handleReady}
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          onTimeUpdate={handleProgress}
          onDurationChange={handleDuration}
          onError={handleError}
          config={{
            youtube: {
              disablekb: 1,
              fs: 0,
              iv_load_policy: 3,
              rel: 0,
            },
          }}
        />
        <div
          className="cd-youtube-top-blocker"
          aria-hidden="true"
        />
        <div
          className="cd-custom-player-activity-overlay"
          aria-hidden="true"
        />

        {state === "loading" && (
          <div
            className="cd-custom-player-message cd-custom-player-loading"
            role="status"
            aria-label="Loading video"
          />
        )}

        {state === "error" && (
          <div
            className="cd-custom-player-message cd-custom-player-error"
            role="alert"
          >
            {error}
          </div>
        )}

        {state === "ready" && (
          <>
            {/* CENTER CONTROLS */}

            <div className="cd-custom-player-center-controls">

              <button
                type="button"
                className="cd-custom-player-skip-button"
                onClick={() => seekBy(-10)}
                aria-label="Rewind 10 seconds"
                title="Rewind 10 seconds"
              >
                <PlayerIcon name="rewind" />
              </button>

              <button
                type="button"
                className="cd-custom-player-center-play"
                onClick={togglePlay}
                aria-label={playing ? "Pause video" : "Play video"}
                title={playing ? "Pause video" : "Play video"}
              >
                <PlayerIcon name={playing ? "pause" : "play"} />
              </button>

              <button
                type="button"
                className="cd-custom-player-skip-button"
                onClick={() => seekBy(10)}
                aria-label="Forward 10 seconds"
                title="Forward 10 seconds"
              >
                <PlayerIcon name="forward" />
              </button>

            </div>

            {/* BOTTOM CONTROLS */}

            <div className="cd-custom-player-controls">

              {/* SEEK BAR */}

              <input
                className="cd-custom-player-seek"
                type="range"
                min="0"
                max="100"
                value={
                  duration
                    ? Math.round(
                        (currentTime / duration) * 100
                      )
                    : 0
                }
                onChange={seek}
                aria-label="Video progress"
                style={{
                  "--cd-player-progress": `${
                    duration
                      ? (currentTime / duration) * 100
                      : 0
                  }%`,
                }}
              />

              <div className="cd-custom-player-control-row">

                {/* LEFT */}

                <div className="cd-custom-player-control-group">

                  <button
                    type="button"
                    onClick={togglePlay}
                    aria-label={
                      playing
                        ? "Pause video"
                        : "Play video"
                    }
                  >
                    <PlayerIcon
                      name={
                        playing
                          ? "pause"
                          : "play"
                      }
                    />
                  </button>

                  <span>
                    {formatTime(currentTime)} /{" "}
                    {formatTime(duration)}
                  </span>

                </div>

                {/* RIGHT */}

                <div className="cd-custom-player-control-group">

                  {/* VOLUME */}

                  <button
                    type="button"
                    onClick={toggleMute}
                    aria-label={
                      muted
                        ? "Unmute video"
                        : "Mute video"
                    }
                  >
                    <PlayerIcon
                      name={
                        muted
                          ? "muted"
                          : "volume"
                      }
                    />
                  </button>

                  <input
                    className="cd-custom-player-volume"
                    type="range"
                    min="0"
                    max="100"
                    value={muted ? 0 : volume}
                    onChange={changeVolume}
                    aria-label="Volume"
                    aria-valuetext={`${
                      muted ? 0 : volume
                    }%`}
                    style={{
                      "--cd-volume-progress": `${
                        muted ? 0 : volume
                      }%`,
                    }}
                  />

                  {/* SPEED */}

                  {isFullscreen && (
                    <select
                      className="cd-custom-player-select cd-custom-player-speed"
                      value={playbackRate}
                      onChange={changePlaybackRate}
                      aria-label="Playback speed"
                      title="Playback speed"
                    >
                      <option value="0.5">
                        0.5x
                      </option>
                      <option value="0.75">
                        0.75x
                      </option>
                      <option value="1">
                        1x
                      </option>
                      <option value="1.25">
                        1.25x
                      </option>
                      <option value="1.5">
                        1.5x
                      </option>
                      <option value="1.75">
                        1.75x
                      </option>
                      <option value="2">
                        2x
                      </option>
                    </select>
                  )}

                  {/* FULLSCREEN */}

                  <button
                    type="button"
                    className="cd-custom-player-fullscreen"
                    onClick={toggleFullscreen}
                    aria-label={
                      isFullscreen
                        ? "Exit fullscreen"
                        : "Enter fullscreen"
                    }
                    title={
                      isFullscreen
                        ? "Exit fullscreen"
                        : "Fullscreen"
                    }
                  >
                    <PlayerIcon
                      name={
                        isFullscreen
                          ? "exitFullscreen"
                          : "fullscreen"
                      }
                    />
                  </button>

                </div>

              </div>
            </div>
          </>
        )}

      </div>
    </div>
  );
}